<template>
  <div class="mx-3 my-3">
    <AppModal
      v-model="showAssignModal"
      title="Assign default registrar"
      size="lg"
      hide-footer
      @close="onAssignModalHidden"
    >
      <p class="text-xs text-gray mb-3">
        Search and select a backoffice account to set as the default annual returns registrar.
      </p>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
        <div>
          <label class="text-sm text-gray block mb-1">Search by name</label>
          <input
            v-model="filters.name"
            type="text"
            placeholder="Name"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded"
            @keyup.enter="searchAccounts"
          />
        </div>
        <div>
          <label class="text-sm text-gray block mb-1">Search by email</label>
          <input
            v-model="filters.email"
            type="text"
            placeholder="Email"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded"
            @keyup.enter="searchAccounts"
          />
        </div>
        <div class="sm:col-span-2 flex gap-2">
          <button class="btn-primary px-3 py-2 rounded-sm text-white" type="button" @click="searchAccounts">
            <i class="fa fa-search mr-1"></i> Search
          </button>
          <button
            class="px-3 py-2 rounded-sm border border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
            type="button"
            @click="resetFilters"
          >
            <i class="fa fa-undo mr-1"></i> Reset
          </button>
        </div>
      </div>

      <div v-if="accountsLoading" class="h-0.5 bg-primary animate-pulse mb-2"></div>

      <div class="max-h-96 overflow-y-auto">
        <div
          v-for="(account, idx) in accounts"
          :key="String(account.id || idx)"
          class="flex items-center justify-between py-2 px-2 border-b border-gray-200"
          :class="selectedRegistrarId === account.id ? 'bg-blue-50' : 'hover:bg-gray-50'"
        >
          <div>
            <p class="text-sm font-bold uppercase mb-1">{{ registrarName(account) }}</p>
            <p class="text-xs text-gray mb-0">{{ fieldValue(account, "email") || "—" }}</p>
            <p class="text-xs text-gray mb-0">
              {{ fieldValue(account, "phoneNumber") || "—" }}
              <span v-if="fieldValue(account, 'idNumber')"> · {{ fieldValue(account, "idNumber") }}</span>
            </p>
          </div>
          <button
            type="button"
            class="min-w-24 px-3 py-1.5 text-xs border rounded"
            :class="selectedRegistrarId === account.id ? 'border-primary text-primary font-semibold' : 'border-gray-300 text-gray-600'"
            @click="selectAccount(account)"
          >
            <span v-if="selectedRegistrarId === account.id">
              Selected <i class="fa fa-check-circle ml-1"></i>
            </span>
            <span v-else>Select</span>
          </button>
        </div>
        <div v-if="accounts.length === 0 && !accountsLoading" class="bg-gray-100 text-center py-3 rounded">
          <span class="text-sm">No backoffice accounts found</span>
        </div>
      </div>

      <div
        v-if="pagination.totalPages > 1 || pagination.totalElements > accounts.length"
        class="flex flex-wrap items-center justify-between gap-3 mt-2"
      >
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="min-w-24 px-4 py-2 text-sm border rounded"
            :class="currentPage > 1 ? 'border-gray-300 text-primary' : 'border-gray-200 text-gray-400 cursor-not-allowed'"
            :disabled="currentPage <= 1"
            @click="previous"
          >
            Previous
          </button>
          <span class="px-2 py-1 bg-primary text-white text-sm rounded">{{ currentPage }} / {{ pagination.totalPages }}</span>
          <button
            type="button"
            class="min-w-24 px-4 py-2 text-sm border rounded"
            :class="currentPage < pagination.totalPages ? 'border-gray-300 text-primary' : 'border-gray-200 text-gray-400 cursor-not-allowed'"
            :disabled="currentPage >= pagination.totalPages"
            @click="next"
          >
            Next
          </button>
        </div>
        <span class="text-xs font-bold text-gray-600">{{ rangeStart }} – {{ rangeEnd }} of {{ pagination.totalElements }}</span>
      </div>

      <div class="flex justify-end gap-2 mt-3 pt-3 border-t border-gray-200">
        <button class="px-3 py-2 rounded-sm border border-gray-300 text-gray-700" type="button" @click="closeAssignModal">
          Cancel
        </button>
        <button
          class="btn-primary px-3 py-2 rounded-sm text-white"
          type="button"
          :disabled="!selectedRegistrarId || saving"
          @click="saveDefaultRegistrar"
        >
          Assign
          <i v-if="saving" class="fa fa-spinner fa-spin ml-1"></i>
        </button>
      </div>
    </AppModal>

    <div class="mb-2 text-right">
      <button class="btn-primary px-3 py-2 rounded-sm text-white" type="button" @click="openAssignModal">
        <i class="fa fa-user-plus mr-1"></i> Assign
      </button>
    </div>

    <div v-if="currentLoading" class="h-0.5 bg-primary animate-pulse mb-2"></div>

    <table class="min-w-full leading-normal">
      <thead>
      <tr class="border-b border-t border-gray-200">
        <th class="t-header">Name</th>
        <th class="t-header">Email</th>
        <th class="t-header">Phone</th>
        <th class="t-header">ID Number</th>
        <th class="t-header">Status</th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="currentRegistrar" class="border-t border-gray-200 bg-gray-50">
        <td class="t-body font-bold uppercase">{{ registrarName(currentRegistrar) }}</td>
        <td class="t-body">{{ fieldValue(currentRegistrar, "email") || "—" }}</td>
        <td class="t-body">{{ fieldValue(currentRegistrar, "phoneNumber") || "—" }}</td>
        <td class="t-body">{{ fieldValue(currentRegistrar, "idNumber") || "—" }}</td>
        <td class="t-body">
          <span v-if="registrarIsActive" class="text-green-600">Active</span>
          <span v-else-if="registrarIsActive === false" class="text-red-600">Inactive</span>
          <span v-else>—</span>
        </td>
      </tr>
      <tr v-if="!currentLoading && !currentRegistrar">
        <td colspan="5" class="bg-gray-100 text-center py-3">
          <span class="text-sm">No default registrar configured. Click Assign to set one.</span>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { api } from "@/app/services/api.service";
import AppModal from "@/components/ui/AppModal.vue";
import { useAccountStore } from "@/domains/home/services/accounts/stores";
import { objectToQueryParams } from "@/commons/composables/utilities.ts";
import { useToast, useConfirmDialog } from "@/composables";
import type { UserAccount } from "@/domains/home/services/accounts/types";

const REGISTRAR_PATH = "business-registration-v2/default-annual-returns-registrar";

type RegistrarRecord = Record<string, unknown>;

const accountStore = useAccountStore();
const { errorToast, successToast, warningToast } = useToast();
const { showConfirm } = useConfirmDialog();

const currentLoading = ref(false);
const accountsLoading = ref(false);
const saving = ref(false);
const showAssignModal = ref(false);
const currentRegistrar = ref<RegistrarRecord | null>(null);
const accounts = ref<UserAccount[]>([]);
const selectedRegistrarId = ref<string | null>(null);
const currentPage = ref(1);
const perPage = 12;
const pagination = ref({ totalElements: 0, totalPages: 0 });
const filters = ref({ name: "", email: "" });

const rangeStart = computed(() => {
  if (accounts.value.length === 0) return 0;
  return (currentPage.value - 1) * perPage + 1;
});

const rangeEnd = computed(() => Math.min(currentPage.value * perPage, pagination.value.totalElements));

const registrarIsActive = computed(() => {
  const registrar = currentRegistrar.value;
  if (!registrar) return null;
  if (registrar.assignmentStatus != null) {
    return String(registrar.assignmentStatus).toUpperCase() === "ACTIVE";
  }
  if (registrar.enabled === true) return true;
  if (registrar.enabled === false) return false;
  return null;
});

const unwrapPayload = (res: { data: unknown }) => {
  const d = res.data as Record<string, unknown>;
  if (d && typeof d === "object" && Object.prototype.hasOwnProperty.call(d, "data")) {
    return d.data;
  }
  return d;
};

type AssignmentRef = {
  userId: string;
  assignmentId: string | null;
  assignmentStatus: string | null;
};

const isUserProfile = (obj: RegistrarRecord) =>
  obj.givenName != null || obj.surName != null || obj.email != null;

const parseAssignmentRef = (body: unknown): AssignmentRef | null => {
  if (body == null || typeof body !== "object") return null;
  const obj = body as RegistrarRecord;

  if (isUserProfile(obj)) {
    const userId = obj.userId ?? obj.id;
    return userId != null ? { userId: String(userId), assignmentId: null, assignmentStatus: null } : null;
  }

  if (obj.registrar && typeof obj.registrar === "object") {
    const registrar = obj.registrar as RegistrarRecord;
    const userId = registrar.id ?? obj.userId ?? obj.registrarId;
    return userId != null
      ? {
          userId: String(userId),
          assignmentId: obj.id != null ? String(obj.id) : null,
          assignmentStatus: obj.status != null ? String(obj.status) : null,
        }
      : null;
  }

  if (obj.userId != null) {
    return {
      userId: String(obj.userId),
      assignmentId: obj.id != null ? String(obj.id) : null,
      assignmentStatus: obj.status != null ? String(obj.status) : null,
    };
  }

  if (obj.registrarId != null) {
    return {
      userId: String(obj.registrarId),
      assignmentId: obj.id != null ? String(obj.id) : null,
      assignmentStatus: obj.status != null ? String(obj.status) : null,
    };
  }

  return null;
};

const mergeRegistrarProfile = (
  assignment: AssignmentRef,
  profile: unknown,
): RegistrarRecord | null => {
  if (profile != null && typeof profile === "object" && isUserProfile(profile as RegistrarRecord)) {
    const user = profile as RegistrarRecord;
    return {
      ...user,
      id: user.id ?? assignment.userId,
      userId: assignment.userId,
      assignmentId: assignment.assignmentId,
      assignmentStatus: assignment.assignmentStatus,
    };
  }

  if (assignment.assignmentStatus != null) {
    return {
      id: assignment.userId,
      userId: assignment.userId,
      assignmentId: assignment.assignmentId,
      assignmentStatus: assignment.assignmentStatus,
    };
  }

  return null;
};

const fieldValue = (row: RegistrarRecord | UserAccount | null, key: string) => {
  if (!row || typeof row !== "object") return null;
  const value = (row as Record<string, unknown>)[key];
  if (value == null || value === "") return null;
  if (key === "idNumber" || key === "email" || key === "phoneNumber") {
    return String(value).split(",")[0];
  }
  return value;
};

const registrarName = (account: RegistrarRecord | UserAccount | null) => {
  if (!account || typeof account !== "object") return "—";
  const obj = account as Record<string, unknown>;
  const parts = [
    obj.givenName,
    obj.middleName && obj.middleName !== "null" ? obj.middleName : null,
    obj.surName,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : "—";
};

const fetchCurrentRegistrar = async () => {
  currentLoading.value = true;
  try {
    const response = await api().get(REGISTRAR_PATH);
    const payload = unwrapPayload(response);
    const assignment = parseAssignmentRef(payload);

    if (!assignment) {
      currentRegistrar.value = null;
      return;
    }

    if (payload != null && typeof payload === "object" && isUserProfile(payload as RegistrarRecord)) {
      currentRegistrar.value = mergeRegistrarProfile(assignment, payload);
      return;
    }

    try {
      const userResponse = await api().get(`auth/api/users/${assignment.userId}`);
      const userPayload = unwrapPayload(userResponse) ?? userResponse.data;
      currentRegistrar.value = mergeRegistrarProfile(assignment, userPayload);
    } catch {
      currentRegistrar.value = mergeRegistrarProfile(assignment, null);
    }
  } catch (error: unknown) {
    currentRegistrar.value = null;
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    errorToast("Error", err.response?.data?.message || err.message || "Failed to load default registrar");
  } finally {
    currentLoading.value = false;
  }
};

const buildAccountFilters = () => {
  const query: Record<string, string | number> = {
    accountType: "BACKOFFICE",
    page: currentPage.value,
    perPage,
  };
  const name = filters.value.name.trim();
  const email = filters.value.email.trim();
  if (name) query.name = name;
  if (email) query.email = email;
  return query;
};

const fetchAccounts = () => {
  accountsLoading.value = true;
  accountStore.fetchStaffAccounts(objectToQueryParams(buildAccountFilters()));
};

const searchAccounts = () => {
  currentPage.value = 1;
  fetchAccounts();
};

const resetFilters = () => {
  filters.value = { name: "", email: "" };
  currentPage.value = 1;
  fetchAccounts();
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    fetchAccounts();
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchAccounts();
  } else {
    warningToast("Observation", "No more data to show");
  }
};

const openAssignModal = () => {
  filters.value = { name: "", email: "" };
  currentPage.value = 1;
  const registrarUserId = currentRegistrar.value?.userId ?? currentRegistrar.value?.id;
  selectedRegistrarId.value = registrarUserId != null ? String(registrarUserId) : null;
  showAssignModal.value = true;
  fetchAccounts();
};

const closeAssignModal = () => {
  showAssignModal.value = false;
};

const onAssignModalHidden = () => {
  accounts.value = [];
  accountsLoading.value = false;
  saving.value = false;
};

const selectAccount = (account: UserAccount) => {
  selectedRegistrarId.value = account.id;
};

const saveDefaultRegistrar = async () => {
  if (!selectedRegistrarId.value) {
    warningToast("Validation", "Please select a backoffice account");
    return;
  }
  const confirmed = await showConfirm("Set this account as the default annual returns registrar?");
  if (!confirmed) return;

  saving.value = true;
  api()
    .post(REGISTRAR_PATH, { registrarId: selectedRegistrarId.value })
    .then((response) => {
      successToast("Success", response.data?.message || "Default registrar updated successfully");
      closeAssignModal();
      fetchCurrentRegistrar();
    })
    .catch((error) => {
      errorToast("Error", error.response?.data?.message || error.message || "Failed to update default registrar");
    })
    .finally(() => {
      saving.value = false;
    });
};

const refresh = () => fetchCurrentRegistrar();

defineExpose({ refresh });

watch(() => accountStore.officer_accounts, (data) => {
  accountsLoading.value = false;
  if (data) {
    accounts.value = data;
    pagination.value = {
      totalPages: accountStore.pagination?.data.totalPages || 0,
      totalElements: accountStore.pagination?.data.totalElements || 0,
    };
  } else {
    accounts.value = [];
    pagination.value = { totalElements: 0, totalPages: 0 };
  }
});

onMounted(() => refresh());
</script>
